/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect, useState } from 'react'
import { Box, Dialog, DialogActions, DialogContent } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { ConfirmationDialog } from '../../../../components/confirmation_dialog'
import { LoadingSpinner } from '../../../../components/loading_spinner'
import { StyledTextField } from '../../../restaurant_groups/styles'
import {
  StyledCancelButton,
  StyledConfirmButton,
  StyledDialogContentText,
  StyledDialogTitle,
} from '../../../../components/confirmation_dialog/styles'
import { formatEmployeeName } from '../../../../utils/employeeLabel'

const CONFIRM_STEP = 'confirm'
const REASON_STEP = 'reason'

const IgnoreEmployeeDialog = ({ open, employees = [], loading, onClose, onSubmit }) => {
  const [step, setStep] = useState(CONFIRM_STEP)
  const [reason, setReason] = useState('')
  const employeeCount = employees.length
  const primaryEmployee = employees?.[0]
  const isBulkAction = employeeCount > 1

  useEffect(() => {
    if (!open) {
      setStep(CONFIRM_STEP)
      setReason('')
      return
    }

    setStep(CONFIRM_STEP)
    setReason('')
  }, [open, employeeCount, primaryEmployee?.employee_guid])

  const handleClose = () => {
    setStep(CONFIRM_STEP)
    setReason('')
    onClose()
  }

  return (
    <>
      <ConfirmationDialog
        open={open && step === CONFIRM_STEP}
        onClose={handleClose}
        Icon={CheckIcon}
        dialogTitle={isBulkAction ? `Are you sure you want to ignore these ${employeeCount} employees?` : 'Are you sure you want to ignore this employee?'}
        dialogBody={isBulkAction
          ? `This action will ignore ${employeeCount} selected employees from this report.`
          : `This action will ignore ${formatEmployeeName(primaryEmployee)} from this report.`}
        confirmationText={isBulkAction ? 'Yes, I want to ignore these employees' : 'Yes, I want to ignore this employee'}
        handleConfirm={() => setStep(REASON_STEP)}
        isLoading={loading}
      />

      <Dialog
        open={open && step === REASON_STEP}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '8px 8px 4px',
            boxShadow: '0px 18px 50px rgba(17, 24, 39, 0.18)',
          }
        }}
      >
        <StyledDialogTitle>
          Add reason for ignoring the employee
        </StyledDialogTitle>
        <DialogContent sx={{ pt: '8px !important', pb: 1 }}>
          <StyledDialogContentText>
            Add a reason for ignoring this employee. The reason is optional.
          </StyledDialogContentText>
          <Box sx={{ mt: 2 }}>
          <StyledTextField
            fullWidth
            multiline
            rows={4}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            variant="outlined"
            inputProps={{
              'data-testid': 'ignore-reason-field',
            }}
            InputProps={{
              sx: {
                alignItems: 'flex-start',
                borderRadius: '12px',
                backgroundColor: '#ffffff',
              }
            }}
            placeholder={isBulkAction ? 'Add reason for ignoring the selected employees' : 'Add reason for ignoring the employee'}
            helperText="Optional. Leave blank if no reason is needed."
          />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1, justifyContent: 'flex-end', gap: 1.5 }}>
          <StyledCancelButton onClick={handleClose}>
            Cancel
          </StyledCancelButton>
          <StyledConfirmButton
            variant="contained"
            onClick={() => onSubmit(reason)}
            disabled={loading}
            sx={{ minWidth: '68px' }}
          >
            {loading ? <LoadingSpinner isLoading={true} /> : 'Save'}
          </StyledConfirmButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default IgnoreEmployeeDialog
